import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { GoogleMap, DrawingManager, Polygon } from '@react-google-maps/api';
import { Button } from '../components/Button';
import { Card, CardHeader, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { ChipSelect } from '../components/ChipSelect';
import { regionAPI } from '../api/regionAPI';
import { userAPI } from '../api/userAPI';
import { useGoogleMapsLoader } from '../hooks/useGoogleMapsLoader';

const boundaryMapStyle = { width: '100%', height: '400px' };
const defaultCenter = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_COLOR = '#34A853';

// Douglas-Peucker polyline simplification
function simplifyPath(points, tolerance) {
    if (points.length <= 2) return points;
    const sqDist = (p, a, b) => {
        let dx = b.lng - a.lng, dy = b.lat - a.lat;
        if (dx !== 0 || dy !== 0) {
            const t = Math.max(0, Math.min(1, ((p.lng - a.lng) * dx + (p.lat - a.lat) * dy) / (dx * dx + dy * dy)));
            dx = a.lng + t * dx; dy = a.lat + t * dy;
        } else { dx = a.lng; dy = a.lat; }
        dx = p.lng - dx; dy = p.lat - dy;
        return dx * dx + dy * dy;
    };
    let maxDist = 0, maxIdx = 0;
    for (let i = 1; i < points.length - 1; i++) {
        const d = sqDist(points[i], points[0], points[points.length - 1]);
        if (d > maxDist) { maxDist = d; maxIdx = i; }
    }
    if (maxDist > tolerance * tolerance) {
        const left = simplifyPath(points.slice(0, maxIdx + 1), tolerance);
        const right = simplifyPath(points.slice(maxIdx), tolerance);
        return left.slice(0, -1).concat(right);
    }
    return [points[0], points[points.length - 1]];
}

export function RegionDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const isNew = !id || id === 'new';

    const [form, setForm] = useState({ name: '', color: DEFAULT_COLOR, boundary: null, leader_ids: [], assistant_ids: [] });
    const [allLeaders, setAllLeaders] = useState([]);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const drawnPolygonRef = useRef(null);
    const existingPolygonRef = useRef(null);
    const [snapToRoads, setSnapToRoads] = useState(true);
    const [snapping, setSnapping] = useState(false);
    const [editingExisting, setEditingExisting] = useState(false);

    const { isLoaded: mapsAvailable } = useGoogleMapsLoader();

    useEffect(() => {
        userAPI.getUsers({ per_page: 1000 }).then((res) => {
            setAllLeaders((res.data.data || {}).items || []);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        regionAPI.getRegion(id).then((res) => {
            const r = res.data.data || res.data;
            const boundary = r.boundary ? (typeof r.boundary === 'string' ? JSON.parse(r.boundary) : r.boundary) : null;
            const leaderIds = (r.leaders || []).map(l => l.id);
            const assistantIds = (r.assistants || []).map(a => a.id);
            setForm({ name: r.name, color: r.color || DEFAULT_COLOR, boundary, leader_ids: leaderIds, assistant_ids: assistantIds });
        }).catch((err) => {
            setError(err.response?.data?.message || 'Failed to load region');
        }).finally(() => setLoading(false));
    }, [id, isNew]);

    const availableLeaders = allLeaders.filter(l => l.roles?.includes('regional_leader'));
    const availableAssistants = allLeaders.filter(l => l.roles?.includes('regional_assistant'));
    const formColor = form.color || DEFAULT_COLOR;

    const clearDrawnPolygon = () => {
        if (drawnPolygonRef.current) {
            drawnPolygonRef.current.setMap(null);
            drawnPolygonRef.current = null;
        }
    };

    const syncExistingPolygon = useCallback(() => {
        const poly = existingPolygonRef.current;
        if (!poly) return;
        const path = poly.getPath();
        const coords = [];
        for (let i = 0; i < path.getLength(); i++) {
            const p = path.getAt(i);
            coords.push({ lat: p.lat(), lng: p.lng() });
        }
        setForm((prev) => ({ ...prev, boundary: coords }));
    }, []);

    const onExistingPolygonLoad = useCallback((polygon) => {
        existingPolygonRef.current = polygon;
        const path = polygon.getPath();
        window.google.maps.event.addListener(path, 'set_at', syncExistingPolygon);
        window.google.maps.event.addListener(path, 'insert_at', syncExistingPolygon);
        window.google.maps.event.addListener(path, 'remove_at', syncExistingPolygon);
        polygon.addListener('rightclick', (e) => {
            if (e.vertex != null && path.getLength() > 3) {
                path.removeAt(e.vertex);
            }
        });
    }, [syncExistingPolygon]);

    const onExistingPolygonUnmount = useCallback(() => {
        existingPolygonRef.current = null;
    }, []);

    const snapBoundaryToRoads = async (vertices) => {
        const ds = new window.google.maps.DirectionsService();
        const snappedPoints = [];
        for (let i = 0; i < vertices.length; i++) {
            const origin = vertices[i];
            const destination = vertices[(i + 1) % vertices.length];
            try {
                const result = await new Promise((resolve, reject) => {
                    ds.route({ origin, destination, travelMode: window.google.maps.TravelMode.DRIVING }, (res, status) => {
                        if (status === 'OK') resolve(res); else reject(new Error(status));
                    });
                });
                const pathPoints = result.routes[0].overview_path.map((p) => ({ lat: p.lat(), lng: p.lng() }));
                snappedPoints.push(...pathPoints.slice(0, -1));
            } catch {
                snappedPoints.push({ lat: origin.lat, lng: origin.lng });
            }
        }
        return snappedPoints;
    };

    const onPolygonComplete = useCallback((polygon) => {
        clearDrawnPolygon();
        drawnPolygonRef.current = polygon;
        const path = polygon.getPath();
        const coords = [];
        for (let i = 0; i < path.getLength(); i++) {
            const point = path.getAt(i);
            coords.push({ lat: point.lat(), lng: point.lng() });
        }
        if (snapToRoads) {
            setSnapping(true);
            snapBoundaryToRoads(coords)
                .then((snapped) => {
                    setForm((prev) => ({ ...prev, boundary: snapped }));
                    if (drawnPolygonRef.current) { drawnPolygonRef.current.setMap(null); drawnPolygonRef.current = null; }
                })
                .catch(() => setForm((prev) => ({ ...prev, boundary: coords })))
                .finally(() => setSnapping(false));
        } else {
            setForm((prev) => ({ ...prev, boundary: coords }));
        }
    }, [snapToRoads]);

    const handleClearBoundary = () => {
        clearDrawnPolygon();
        existingPolygonRef.current = null;
        setEditingExisting(false);
        setForm((prev) => ({ ...prev, boundary: null }));
    };

    const handleSimplify = () => {
        if (!form.boundary || form.boundary.length < 4) return;
        // tolerance ~0.001 degree ≈ 100m — keeps shape recognizable with far fewer points
        const simplified = simplifyPath(form.boundary, 0.001);
        setForm((prev) => ({ ...prev, boundary: simplified }));
    };

    const handleDoneEditing = async () => {
        setEditingExisting(false);
        if (snapToRoads && form.boundary && form.boundary.length >= 3) {
            setSnapping(true);
            try {
                const snapped = await snapBoundaryToRoads(form.boundary);
                setForm((prev) => ({ ...prev, boundary: snapped }));
            } catch {}
            setSnapping(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { name: form.name, color: form.color, leader_ids: form.leader_ids, assistant_ids: form.assistant_ids };
            if (form.boundary) payload.boundary = form.boundary;
            if (isNew) {
                await regionAPI.createRegion(payload);
                setSuccess('Region created');
            } else {
                await regionAPI.updateRegion(id, payload);
                setSuccess('Region updated');
            }
            setTimeout(() => navigate('/regions'), 600);
        } catch (err) {
            setError(err.response?.data?.message || 'Operation failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Delete this region?')) return;
        try {
            await regionAPI.deleteRegion(id);
            navigate('/regions');
        } catch (err) {
            setError('Failed to delete region');
        }
    };

    if (!isAdmin) {
        return (
            <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
                <Card><CardBody><h2>Access Denied</h2><p>Only administrators can manage regions.</p><Button onClick={() => navigate('/dashboard')}>Back</Button></CardBody></Card>
            </div>
        );
    }

    if (loading) {
        return <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in"><p className="text-center text-slate-500 py-6">Loading...</p></div>;
    }

    return (
        <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
            <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">{isNew ? 'Create Region' : 'Edit Region'}</h1>
                </div>
                <div>
                    <Button variant="secondary" onClick={() => navigate('/regions')}>Back</Button>
                </div>
            </header>

            <main>
                {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
                {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

                <Card>
                    <CardHeader>
                        <h2>{isNew ? 'New Region' : 'Region Details'}</h2>
                    </CardHeader>
                    <CardBody>
                        <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Name *</label>
                                <input className="input-field" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Color</label>
                                <div className="flex items-center gap-2">
                                    <input type="color" value={formColor} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-10 h-9 p-0.5 cursor-pointer border border-slate-200 dark:border-slate-700 rounded" />
                                    <span className="text-sm text-slate-500">{formColor}</span>
                                </div>
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Regional Leaders ({form.leader_ids.length} selected)</label>
                                <ChipSelect
                                    options={availableLeaders.map(l => ({ value: l.id, label: `${l.name} (${l.email})` }))}
                                    selected={form.leader_ids}
                                    onChange={(ids) => setForm({ ...form, leader_ids: ids })}
                                    placeholder="Search leaders..."
                                    emptyMessage="No regional leaders available. Create leaders first."
                                />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Regional Assistants ({form.assistant_ids.length} selected)</label>
                                <ChipSelect
                                    options={availableAssistants.map(l => ({ value: l.id, label: `${l.name} (${l.email})` }))}
                                    selected={form.assistant_ids}
                                    onChange={(ids) => setForm({ ...form, assistant_ids: ids })}
                                    placeholder="Search assistants..."
                                    emptyMessage="No regional assistants available. Create assistants first."
                                />
                            </div>

                            {mapsAvailable && (
                                <div className="flex flex-col gap-1.5 sm:col-span-2">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Boundary {form.boundary ? `(${form.boundary.length} points)` : ''}</label>
                                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                        <input type="checkbox" id="snap-to-roads" checked={snapToRoads} onChange={(e) => setSnapToRoads(e.target.checked)} />
                                        <label htmlFor="snap-to-roads">Snap to roads</label>
                                    </div>
                                    <div className="rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                                        <GoogleMap
                                            mapContainerStyle={boundaryMapStyle}
                                            center={form.boundary && form.boundary.length > 0 ? { lat: form.boundary[0].lat, lng: form.boundary[0].lng } : defaultCenter}
                                            zoom={form.boundary ? 10 : 4}
                                        >
                                            {(!form.boundary || !editingExisting) && (
                                                <DrawingManager
                                                    onPolygonComplete={onPolygonComplete}
                                                    options={{
                                                        drawingControl: !editingExisting,
                                                        drawingControlOptions: { drawingModes: ['polygon'] },
                                                        polygonOptions: { fillColor: formColor, fillOpacity: 0.3, strokeColor: formColor, strokeWeight: 2, editable: true },
                                                    }}
                                                />
                                            )}
                                            {form.boundary && Array.isArray(form.boundary) && form.boundary.length >= 3 && !drawnPolygonRef.current && (
                                                <Polygon
                                                    paths={form.boundary}
                                                    options={{ fillColor: formColor, fillOpacity: 0.3, strokeColor: formColor, strokeWeight: 2, editable: editingExisting, draggable: editingExisting }}
                                                    onLoad={editingExisting ? onExistingPolygonLoad : undefined}
                                                    onUnmount={onExistingPolygonUnmount}
                                                />
                                            )}
                                        </GoogleMap>
                                    </div>
                                    {snapping && <div className="text-sm text-slate-500 py-1">Snapping to roads...</div>}
                                    {editingExisting && <p className="text-xs text-slate-500 mt-1">Drag vertices to move. Drag midpoints to add. Right-click a vertex to remove.</p>}
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {form.boundary && !editingExisting && (
                                            <Button type="button" variant="secondary" size="sm" onClick={() => setEditingExisting(true)}>Edit Boundary</Button>
                                        )}
                                        {form.boundary && !editingExisting && form.boundary.length > 20 && (
                                            <Button type="button" variant="secondary" size="sm" onClick={handleSimplify}>
                                                Simplify ({form.boundary.length} pts)
                                            </Button>
                                        )}
                                        {editingExisting && (
                                            <Button type="button" variant="secondary" size="sm" onClick={handleDoneEditing}>Done Editing{snapToRoads ? ' & Re-snap' : ''}</Button>
                                        )}
                                        {form.boundary && (
                                            <Button type="button" variant="secondary" size="sm" onClick={handleClearBoundary}>Clear Boundary</Button>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3 sm:col-span-2 mt-2">
                                <Button type="submit" disabled={saving}>{saving ? 'Saving...' : (isNew ? 'Create' : 'Update')}</Button>
                                <Button variant="secondary" type="button" onClick={() => navigate('/regions')}>Cancel</Button>
                            </div>
                        </form>
                    </CardBody>
                </Card>

                {!isNew && (
                    <div className="card-base border-red-200 dark:border-red-800 p-4 mt-6">
                        <h3 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-3">Danger Zone</h3>
                        <div className="flex flex-wrap gap-2">
                            <Button variant="danger" onClick={handleDelete}>Delete Region</Button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

export default RegionDetail;
